import React, { useState, useEffect, useMemo } from 'react';
import { 
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, TextField, Select, MenuItem, FormControl, InputLabel,
    IconButton, Tooltip, Chip, Fab, useMediaQuery, useTheme, Divider
} from '@mui/material';
import { 
    MdEdit, MdPersonAdd, MdCheckCircle, MdCancel
} from "react-icons/md";
import axios from 'axios';
import { API_BASE_URL } from '../../App.js';
import MemberModal from './components/MemberModal';
import ImportModal from './components/ImportModal';
import { RiUpload2Line } from "react-icons/ri";

const Members = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [players, setPlayers] = useState([]);
    const [show, setShow] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [roleFilter, setRoleFilter] = useState('all');
    const [showImport, setShowImport] = useState(false);

    const fetchPlayers = () => {
        axios.get(`${API_BASE_URL}/api/allplayersadmin`)
            .then((response) => setPlayers(response.data))
            .catch((error) => {
                console.error("Es gab ein Problem beim Abrufen der Daten: ", error);
            });
    };

    useEffect(() => {
        fetchPlayers();
    }, []);

    const filteredPlayers = useMemo(() =>
        players
            .filter(player => {
                const guestEmail = (process.env.REACT_APP_GUEST_EMAIL || 'gast@tc-opfingen.de').toLowerCase();
                if (player.email && player.email.toLowerCase() === guestEmail) return false;

                const matchesSearch = (
                    player.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    player.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    player.email?.toLowerCase().includes(searchTerm.toLowerCase())
                );

                let matchesStatus = true;
                if (statusFilter === 'active') matchesStatus = player.isActive;
                if (statusFilter === 'inactive') matchesStatus = !player.isActive;

                let matchesRole = true;
                if (roleFilter === 'admin') matchesRole = player.role === 2;
                if (roleFilter === 'trainer') matchesRole = player.role === 1;
                if (roleFilter === 'member') matchesRole = player.role === 0;

                return matchesSearch && matchesStatus && matchesRole;
            })
        , [players, searchTerm, statusFilter, roleFilter]);

    const closeModal = () => {
        setShow(false);
        setEditMode(false);
        setSelectedPlayer(null);
    };

    const showCreateModal = () => {
        setShow(true);
        setEditMode(false);
        setSelectedPlayer(null);
    };

    const showEditModal = (player) => {
        setShow(true);
        setEditMode(true);
        setSelectedPlayer(player);
    };

    const getRoleColor = (role) => {
        if (role === 2) return theme.palette.error.main;
        if (role === 1) return theme.palette.info.main;
        return 'inherit';
    };

    return (
        <Box>
            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', lg: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', lg: 'center' },
                    gap: 2,
                    mb: 2
                }}>
                    <Typography variant="h5" component="h1" color="primary" fontWeight="bold">
                        Mitgliederverwaltung
                    </Typography>

                    <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 2, 
                        alignItems: 'center',
                        width: { xs: '100%', lg: 'auto' },
                        justifyContent: { xs: 'flex-start', lg: 'flex-end' } 
                    }}>
                        <TextField
                            placeholder="Suchen..."
                            variant="outlined"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 200 }, maxWidth: { lg: 300 } }}
                        />

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}>
                            <FormControl size="small" sx={{ minWidth: 110 }}>
                                <InputLabel id="status-filter-label">Status</InputLabel>
                                <Select
                                    labelId="status-filter-label"
                                    value={statusFilter}
                                    label="Status"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="all">Alle</MenuItem>
                                    <MenuItem value="active">Aktiv</MenuItem>
                                    <MenuItem value="inactive">Inaktiv</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 110 }}>
                                <InputLabel id="role-filter-label">Rolle</InputLabel>
                                <Select
                                    labelId="role-filter-label"
                                    value={roleFilter}
                                    label="Rolle"
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                >
                                    <MenuItem value="all">Alle</MenuItem>
                                    <MenuItem value="member">Mitglied</MenuItem>
                                    <MenuItem value="trainer">Trainer</MenuItem>
                                    <MenuItem value="admin">Admin</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, mx: 1 }} />

                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                            <Tooltip title="Mitglieder importieren">
                                <IconButton 
                                    color="primary" 
                                    onClick={() => setShowImport(true)}
                                    sx={{ 
                                        border: '1.5px solid',
                                        borderColor: 'primary.light',
                                        p: 0.5,
                                        '&:hover': { bgcolor: 'primary.light', color: 'white' }
                                    }}
                                >
                                    <RiUpload2Line size={20} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Mitglied neu anlegen">
                                <Fab 
                                    color="primary" 
                                    size="small" 
                                    onClick={showCreateModal}
                                    sx={{ 
                                        boxShadow: 2,
                                        '&:hover': { transform: 'scale(1.05)' },
                                        transition: 'transform 0.2s'
                                    }}
                                >
                                    <MdPersonAdd size={20} />
                                </Fab>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                    <TableRow>
                        <TableCell sx={{ 
                            fontWeight: 'bold', 
                            px: isMobile ? 1 : 2, 
                            fontSize: isMobile ? '0.75rem' : '0.85rem' 
                        }}>Name</TableCell>
                        {!isMobile && <TableCell sx={{ fontWeight: 'bold' }}>E-Mail</TableCell>}
                        {!isMobile && <TableCell align="center" sx={{ fontWeight: 'bold' }}>Buchungen</TableCell>}
                        <TableCell align="center" sx={{ 
                            fontWeight: 'bold', 
                            px: isMobile ? 0.5 : 2, 
                            fontSize: isMobile ? '0.75rem' : '0.85rem' 
                        }}>Status</TableCell>
                        <TableCell align="right" sx={{ 
                            fontWeight: 'bold', 
                            px: isMobile ? 0.5 : 2, 
                            fontSize: isMobile ? '0.75rem' : '0.85rem' 
                        }}>Aktion</TableCell>
                    </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPlayers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">Keine Mitglieder gefunden.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredPlayers.map((player) => (
                                    <TableRow key={player._id} hover>
                                        <TableCell sx={{ px: isMobile ? 1 : 2 }}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontWeight: 600,
                                                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                                                    color: getRoleColor(player.role)
                                                }}
                                            >
                                                {player.lastName} {player.firstName}
                                            </Typography>
                                            {isMobile && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                    {player.email}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        {!isMobile && <TableCell sx={{ fontSize: '0.85rem' }}>{player.email}</TableCell>}
                                        {!isMobile && <TableCell align="center">{player.bookingTimes || 0}</TableCell>}
                                        <TableCell align="center" sx={{ px: isMobile ? 0.5 : 2 }}>
                                            <Chip 
                                                label={isMobile ? "" : (player.isActive ? "Aktiv" : "Inaktiv")} 
                                                size="small"
                                                color={player.isActive ? "success" : "error"}
                                                variant="outlined"
                                                icon={player.isActive ? <MdCheckCircle /> : <MdCancel />}
                                                sx={{ 
                                                    minWidth: isMobile ? 'auto' : 80,
                                                    height: isMobile ? 24 : 32,
                                                    '& .MuiChip-icon': {
                                                        margin: isMobile ? '0 auto' : '0 0 0 8px'
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ px: isMobile ? 0.5 : 2 }}>
                                            <Tooltip title="Bearbeiten">
                                                <IconButton 
                                                    size="small" 
                                                    color="primary" 
                                                    onClick={() => showEditModal(player)}
                                                    sx={{ p: isMobile ? 0.5 : 1 }}
                                                >
                                                    <MdEdit size={isMobile ? 18 : 20} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <MemberModal
                show={show}
                closeModal={closeModal}
                setPlayers={setPlayers}
                editMode={editMode}
                playerToEdit={selectedPlayer}
            />
            <ImportModal
                open={showImport}
                onClose={() => setShowImport(false)}
                onRefresh={fetchPlayers}
            />
        </Box>
    );
};

export default Members;;
